import React from 'react';
import { DateRangePicker as DateRangeCalendar } from 'react-date-range';
import { Modal, Button } from 'react-bootstrap';
import { enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import useDateRangePicker from './hooks/useDateRangePicker';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { BRAND_COLOR } from 'constants/color.constants';

const formatLabel = (value, placeholder) => {
  if (!value?.start || !value?.end) return placeholder;

  return `${format(value.start, 'MMM d, yyyy')} - ${format(value.end, 'MMM d, yyyy')}`;
};

/**
 * Clickable input that opens a floating date-range calendar on desktop and a
 * modal on mobile.
 *
 * @param {{ start: Date, end: Date }} value   Currently committed range.
 * @param {(range: { start: Date, end: Date }) => void} onChange  Called on Apply.
 * @param {string} [placeholder]  Shown when no range is selected.
 * @param {string} [rangeColor]   Highlight color for the selected range.
 * @param {number} [maxSpanDays]  Widest selectable range; unlimited when omitted.
 */
function DateRangePicker({
  value,
  onChange,
  placeholder = 'Select date range',
  rangeColor = BRAND_COLOR,
  maxSpanDays,
}) {
  const {
    isOpen,
    isLargeScreen,
    draft,
    containerRef,
    rangeTooWide,
    toggle,
    close,
    handleSelect,
    apply,
  } = useDateRangePicker({ value, onChange, maxSpanDays });

  const calendar = (
    <DateRangeCalendar
      ranges={[draft]}
      onChange={handleSelect}
      locale={enUS}
      months={isLargeScreen ? 1 : 2}
      direction={isLargeScreen ? 'vertical' : 'horizontal'}
      moveRangeOnFirstSelection={false}
      rangeColors={[rangeColor]}
    />
  );

  const actions = (
    <>
      {/* me-auto keeps the buttons right-aligned and takes no space when absent. */}
      {rangeTooWide && (
        <span className="date_range_picker__limit text-danger small me-auto">
          {`Please select a range of ${maxSpanDays} days or less.`}
        </span>
      )}
      <Button
        variant="link"
        size="sm"
        onClick={close}
      >
        Cancel
      </Button>
      <Button size="sm" onClick={apply} disabled={rangeTooWide}>
        Apply
      </Button>
    </>
  );

  return (
    <div className="date_range_picker" ref={containerRef}>
      <button
        type="button"
        className="date_range_picker__input"
        onClick={toggle}
      >
        <span>{formatLabel(value, placeholder)}</span>

        <i class="fa fa-solid fa-caret-down ms-2"></i>
      </button>

      {/* Desktop: floating panel anchored to the input */}
      {isOpen && !isLargeScreen && (
        <div className="date_range_picker__popup box-shadow">
          {calendar}
          <div className="date_range_picker__actions d-flex justify-content-end">
            {actions}
          </div>
        </div>
      )}

      {/* Mobile: full-width modal */}
      <Modal
        show={isOpen && isLargeScreen}
        onHide={close}
        keyboard
        centered
        className="date_range_picker__modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {placeholder}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body 
          className="d-flex justify-content-center"
        >
          {calendar}
        </Modal.Body>

        <Modal.Footer>{actions}</Modal.Footer>
      </Modal>
    </div>
  );
}

export default DateRangePicker;
